import { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { useFamilyPortalApiClient } from '../../auth/hooks/useFamilyPortalApiClient'
import { buildConsentConfigResponse, type ConsentConfigPayload } from '../../consent/consentConfig'
import {
  answersFromSecondaryState,
  isOutstandingSecondaryElection,
} from '../../consent/consentOptions/consentOptionsUtils'
import type {
  ConsentContext,
  SecondaryConsentRow,
  SecondaryConsentStateResponse,
  SecondaryElection,
} from '../../consent/consentOptions/types'
import {
  scopedStateKey,
  secondaryAnswerSubjects,
  subjectStateKey,
  type ConsentSubject,
} from '../../consent/consentSubjects'

export type OutstandingSecondaryConsentItem = {
  id: string
  title: string
}

type OutstandingSecondaryConsentsState = {
  loading: boolean
  items: OutstandingSecondaryConsentItem[]
}

type OutstandingAnswer = SecondaryElection | boolean | undefined

function asElection(value: OutstandingAnswer): SecondaryElection | undefined {
  if (value === 'opt_in' || value === 'opt_out' || value === 'unset') return value
  if (value === true) return 'opt_in'
  if (value === false) return 'opt_out'
  return undefined
}

function isOutstanding(
  itemId: string,
  answers: Record<string, OutstandingAnswer>,
  dualSecondary: boolean,
  subjects: ConsentSubject[] | null
): boolean {
  if (subjects) {
    return subjects.some((subject) => answers[subjectStateKey(itemId, subject, dualSecondary)] === undefined)
  }
  if (dualSecondary) {
    return (
      isOutstandingSecondaryElection(asElection(answers[scopedStateKey(itemId, 'SELF')])) ||
      isOutstandingSecondaryElection(asElection(answers[scopedStateKey(itemId, 'DEPENDENT')]))
    )
  }
  return isOutstandingSecondaryElection(asElection(answers[scopedStateKey(itemId)]))
}

export function getOutstandingSecondaryConsentItems(
  rows: SecondaryConsentRow[],
  answers: Record<string, OutstandingAnswer>,
  dualSecondary: boolean,
  /** One answer row per subject when a guardian-covered Sibling is listed; null otherwise. */
  subjects: ConsentSubject[] | null = null
): OutstandingSecondaryConsentItem[] {
  const seen = new Set<string>()
  const outstanding: OutstandingSecondaryConsentItem[] = []

  rows.forEach((row) => {
    if (seen.has(row.item.id)) return
    seen.add(row.item.id)

    if (isOutstanding(row.item.id, answers, dualSecondary, subjects)) {
      outstanding.push({ id: row.item.id, title: row.item.title })
    }
  })

  return outstanding
}

export function useOutstandingSecondaryConsents(enabled: boolean): OutstandingSecondaryConsentsState {
  const intl = useIntl()
  const apiClient = useFamilyPortalApiClient()
  const [state, setState] = useState<OutstandingSecondaryConsentsState>({ loading: false, items: [] })

  useEffect(() => {
    if (!enabled) {
      setState({ loading: false, items: [] })
      return
    }

    let cancelled = false
    setState({ loading: true, items: [] })

    async function load() {
      try {
        const [contextRes, configRes] = await Promise.all([
          apiClient.get('/consents/context', undefined, true),
          apiClient.get('/config/consents/secondary'),
        ])

        if (cancelled) return
        if (!configRes.ok) {
          setState({ loading: false, items: [] })
          return
        }

        const context = contextRes.ok ? ((await contextRes.json().catch(() => null)) as ConsentContext | null) : null
        // Avoid guessing single vs dual-subject consent state without context.
        if (!context) {
          setState({ loading: false, items: [] })
          return
        }

        const configPayload = (await configRes.json()) as ConsentConfigPayload
        const config = buildConsentConfigResponse(configPayload, intl.locale)

        if (config.secondaryItems.length === 0) {
          setState({ loading: false, items: [] })
          return
        }

        const params = new URLSearchParams({
          configId: config.configId,
          configVersionId: config.configVersionId,
          consentType: 'secondary',
        })
        const stateRes = await apiClient.get(`/consents/secondary?${params.toString()}`)

        if (cancelled) return
        if (!stateRes.ok) {
          setState({ loading: false, items: [] })
          return
        }

        const stateData = (await stateRes.json().catch(() => ({}))) as SecondaryConsentStateResponse
        if (cancelled) return

        const dualSecondary = context.secondaryConsentDualSubjects === true
        const subjects = secondaryAnswerSubjects(context, dualSecondary)
        const parsed = answersFromSecondaryState(stateData, subjects)
        const answers = subjects ? parsed.toggles : { ...parsed.toggles, ...parsed.elections }
        const outstanding = getOutstandingSecondaryConsentItems(config.secondaryItems, answers, dualSecondary, subjects)
        setState({ loading: false, items: outstanding })
      } catch {
        if (!cancelled) setState({ loading: false, items: [] })
      }
    }

    load().finally(() => {
      if (!cancelled) {
        setState((current) => (current.loading ? { loading: false, items: current.items } : current))
      }
    })

    return () => {
      cancelled = true
    }
  }, [apiClient, enabled, intl.locale])

  return state
}
